import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from 'react-bootstrap';
import { ArrowLeft, GraphUp } from 'react-bootstrap-icons';
import { useTheme } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';
import { getAppNavRoutes } from '../utils/appNavRoutes';
import { hasTradePlatformAccess } from '../utils/authAccess';
import { DASHBOARD_ROUTES } from '../constants/dashboardRoutes';
import Seo from '../components/Seo';

/**
 * Fallback for unknown paths. Keeps site chrome (logo + nav) and trading-themed copy.
 */
const NotFound = () => {
  const { colors, themeName } = useTheme();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const nav = getAppNavRoutes(user);
  const hasTradeAccess = Boolean(user?.token) && hasTradePlatformAccess(user);

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.bgPrimary,
        color: colors.text,
      }}
    >
      <Seo title="Page not found | PrecisionTrader" noindex />
      <Navbar
        style={{
          backgroundColor: colors.bgSecondary,
          borderBottom: `1px solid ${colors.border}`,
          padding: '8px 16px',
          flexShrink: 0,
        }}
      >
        <Navbar.Brand
          onClick={() => navigate(nav.homePath)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/favicon.png`}
            alt=""
            role="presentation"
            style={{ height: 32, width: 'auto', borderRadius: 6 }}
          />
          <span
            key={themeName}
            className="d-none d-sm-inline"
            style={{
              fontWeight: 600,
              fontSize: 20,
              letterSpacing: 0.5,
              background: `linear-gradient(45deg, ${colors.primary}, ${colors.secondary})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            PrecisionTrader
          </span>
        </Navbar.Brand>

        <div className="ms-auto d-flex align-items-center gap-2">
          {hasTradeAccess ? (
            <>
              <Link
                to={nav.dashboardPath}
                className="btn btn-sm"
                style={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${colors.border}`,
                  color: colors.text,
                  fontSize: 13,
                }}
              >
                Dashboard
              </Link>
              <Link
                to={nav.tradePath}
                className="btn btn-sm"
                style={{
                  backgroundColor: colors.primary,
                  border: 'none',
                  color: '#fff',
                  fontSize: 13,
                }}
              >
                Trade
              </Link>
            </>
          ) : (
            <Link
              to={nav.homePath}
              className="btn btn-sm"
              style={{
                backgroundColor: colors.primary,
                border: 'none',
                color: '#fff',
                fontSize: 13,
              }}
            >
              Home
            </Link>
          )}
        </div>
      </Navbar>

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: 420 }}>
          <div
            style={{
              fontSize: 'clamp(4rem, 12vw, 6rem)',
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '-0.04em',
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: 16,
            }}
          >
            404
          </div>

          <h1 style={{ fontSize: '1.35rem', fontWeight: 600, marginBottom: 10, color: colors.text }}>
            This page stopped out.
          </h1>

          <p style={{ fontSize: '0.95rem', color: colors.textMuted, lineHeight: 1.55, marginBottom: 28 }}>
            The URL you hit doesn&apos;t exist — bad ticker, wrong route, or it got liquidated.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
            {hasTradeAccess ? (
              <>
                <Link
                  to={nav.tradePath}
                  className="btn btn-primary d-inline-flex align-items-center gap-2"
                  style={{ fontWeight: 500 }}
                >
                  <GraphUp size={16} />
                  Back to Trade
                </Link>
                <Link
                  to={DASHBOARD_ROUTES.health}
                  className="btn d-inline-flex align-items-center gap-2"
                  style={{
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`,
                    color: colors.text,
                    fontWeight: 500,
                  }}
                >
                  Account Health
                </Link>
              </>
            ) : (
              <Link to={nav.homePath} className="btn btn-primary" style={{ fontWeight: 500 }}>
                Go home
              </Link>
            )}
            <button
              type="button"
              className="btn d-inline-flex align-items-center gap-2"
              onClick={() => navigate(-1)}
              style={{
                backgroundColor: 'transparent',
                border: `1px solid ${colors.border}`,
                color: colors.textMuted,
                fontWeight: 500,
              }}
            >
              <ArrowLeft size={16} />
              Go back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
